const env = import.meta.env;

export interface EnvConfigurationModel {
  colocaApiUrl: string;
  socketOrigin?: string;
  env: string;
}

/**
 * The API base URL declared for the dashboard host the page is served from, or `null` when the host is not
 * listed.
 *
 * One build is served from several domains (production answers on `app.colocapay.com` and
 * `app.us.colocapayments.com`), and the session travels only in `SameSite=Strict` cookies, which the browser
 * sends to an API on the SAME site as the page. A front calling an API of another site still logs in — the
 * login needs no cookie and CORS lets it through — and then sends every request without one: a 401 that the
 * pages render as "no tienes permiso". So each host names its own API in `VITE_COLOCA_API_BY_HOST`, as
 * comma-separated `host=apiBaseUrl` pairs; a host not listed (localhost, a preview) keeps `VITE_COLOCA_API`.
 */
export function resolveApiUrlForHost(hostname: string, apiByHost: string | undefined): string | null {
  for (const pair of (apiByHost ?? "").split(",")) {
    const separator = pair.indexOf("=");
    if (separator === -1) continue;
    const host = pair.slice(0, separator).trim().toLowerCase();
    const apiUrl = pair.slice(separator + 1).trim();
    if (host && apiUrl && host === hostname.toLowerCase()) return apiUrl;
  }
  return null;
}

export const EnvConfiguration = (): EnvConfigurationModel => {
  const hostname = typeof window === "undefined" ? "" : window.location.hostname;
  const hostApiUrl = resolveApiUrlForHost(hostname, env.VITE_COLOCA_API_BY_HOST);
  return {
    colocaApiUrl: hostApiUrl ?? env.VITE_COLOCA_API,
    // The socket is served by the API host. `VITE_SOCKET_ORIGIN` belongs to the default API, so a host with
    // its own API takes the socket from that API's origin instead.
    socketOrigin: hostApiUrl ? new URL(hostApiUrl).origin : env.VITE_SOCKET_ORIGIN,
    env: env.VITE_ENV,
  };
};

export class ConfigService {
  private static instance: ConfigService;
  private readonly environment: EnvConfigurationModel;

  private constructor() {
    this.environment = EnvConfiguration();
  }

  public static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService();
    }
    return ConfigService.instance;
  }

  get(env: keyof EnvConfigurationModel) {
    return this.environment[env];
  }

  getOrThrow(env: keyof EnvConfigurationModel) {
    const value = this.get(env);
    if (!value) {
      throw new Error(`Environment : ${env} not found or not loaded`);
    }
    return value;
  }
}
